<template>
  <div class="p-6">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">Unidades de Medida</h1>
      <p class="text-gray-600 dark:text-gray-400">Catálogo de unidades de medida POS2</p>
    </div>

    <!-- Tabs -->
    <div class="flex gap-1 mb-4 border-b border-gray-200 dark:border-gray-700">
      <button
        @click="tabActivo = 'catalogo'"
        class="px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors"
        :class="tabActivo === 'catalogo'
          ? 'border-violet-600 text-violet-600 dark:text-violet-400'
          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'"
      >
        Catálogo de Unidades de Medida
      </button>
      <button
        @click="tabActivo = 'equivalencias'"
        class="px-5 py-2.5 text-sm font-semibold border-b-2 transition-colors"
        :class="tabActivo === 'equivalencias'
          ? 'border-violet-600 text-violet-600 dark:text-violet-400'
          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'"
      >
        Equivalencias
      </button>
    </div>

    <!-- ==================== TAB CATÁLOGO ==================== -->
    <div v-if="tabActivo === 'catalogo'" class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div class="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap gap-2 items-center justify-between">
        <div class="flex gap-2">
          <input
            v-model="filtros.buscar"
            @keyup.enter="buscarUnidades"
            type="text"
            placeholder="Buscar por nombre o clave..."
            class="px-3 py-2 rounded border text-sm bg-gray-50 dark:bg-gray-900 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200 w-64"
          />
          <button @click="buscarUnidades" class="px-4 py-2 rounded bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition">Buscar</button>
          <button @click="limpiarUnidades" class="px-4 py-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition">Limpiar</button>
        </div>
        <div class="flex gap-2">
          <button
            v-if="filaEditandoId !== '__nueva__'"
            @click="agregarFilaNueva"
            class="px-4 py-2 rounded bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition flex items-center gap-1"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
            Nueva Unidad
          </button>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-sm text-left">
          <thead class="text-xs uppercase bg-gray-100 dark:bg-gray-800 text-blue-600 dark:text-blue-400 border-b-2 border-gray-200 dark:border-gray-700">
            <tr>
              <th class="px-3 py-2 w-8"><input type="checkbox" class="rounded"/></th>
              <th class="px-3 py-2 w-20">Id Interno</th>
              <th class="px-3 py-2 w-36">Id Unidad Medida</th>
              <th class="px-3 py-2 w-44">Nombre</th>
              <th class="px-3 py-2">Descripción</th>
              <th class="px-3 py-2 w-32">Clave SAT</th>
              <th class="px-3 py-2 w-36">Nombre SAT</th>
              <th class="px-3 py-2 w-20"></th>
            </tr>
          </thead>
          <tbody>
            <!-- Filas existentes -->
            <tr v-if="loadingUnidades">
              <td colspan="8" class="px-4 py-8 text-center text-gray-400">Cargando...</td>
            </tr>
            <template v-else>
              <tr
                v-for="unidad in unidades"
                :key="unidad.id"
                class="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                :class="unidad.id === filaEditandoId ? 'bg-violet-50 dark:bg-violet-900/20' : 'bg-white dark:bg-gray-800'"
              >
                <td class="px-3 py-1.5 text-center"><input type="checkbox" class="rounded"/></td>
                <td class="px-3 py-1.5 text-gray-500 dark:text-gray-400 text-center">{{ unidad.id }}</td>

                <!-- Modo lectura -->
                <template v-if="unidad.id !== filaEditandoId">
                  <td class="px-3 py-1.5 font-mono font-semibold text-gray-900 dark:text-white">{{ unidad.id_unidad_medida }}</td>
                  <td class="px-3 py-1.5 text-gray-900 dark:text-white">{{ unidad.nombre }}</td>
                  <td class="px-3 py-1.5 text-gray-500 dark:text-gray-400">{{ unidad.descripcion }}</td>
                  <td class="px-3 py-1.5 font-mono text-gray-700 dark:text-gray-300">{{ unidad.clave_sat }}</td>
                  <td class="px-3 py-1.5 text-gray-500 dark:text-gray-400">{{ unidad.nombre_sat }}</td>
                  <td class="px-3 py-1.5">
                    <div class="flex gap-1">
                      <button @click="editarFila(unidad)" class="text-blue-600 hover:text-blue-800 dark:text-blue-400 p-1" title="Editar">
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                      </button>
                      <button @click="eliminarUnidad(unidad)" class="text-red-500 hover:text-red-700 dark:text-red-400 p-1" title="Eliminar">
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                      </button>
                    </div>
                  </td>
                </template>

                <!-- Modo edición -->
                <template v-else>
                  <td class="px-1 py-1"><input v-model="filaForm.id_unidad_medida" type="text" maxlength="30" class="w-full px-2 py-1 border border-violet-400 rounded text-sm font-mono dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"/></td>
                  <td class="px-1 py-1"><input v-model="filaForm.nombre" type="text" maxlength="100" class="w-full px-2 py-1 border border-violet-400 rounded text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"/></td>
                  <td class="px-1 py-1"><input v-model="filaForm.descripcion" type="text" maxlength="255" class="w-full px-2 py-1 border border-violet-400 rounded text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"/></td>
                  <td class="px-1 py-1"><input v-model="filaForm.clave_sat" type="text" maxlength="30" class="w-full px-2 py-1 border border-violet-400 rounded text-sm font-mono dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"/></td>
                  <td class="px-1 py-1"><input v-model="filaForm.nombre_sat" type="text" maxlength="100" class="w-full px-2 py-1 border border-violet-400 rounded text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"/></td>
                  <td class="px-1 py-1">
                    <div class="flex gap-1">
                      <button @click="guardarFila" :disabled="guardandoFila" class="text-green-600 hover:text-green-800 dark:text-green-400 p-1" title="Guardar">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                      </button>
                      <button @click="cancelarFila" class="text-gray-400 hover:text-gray-600 p-1" title="Cancelar">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                      </button>
                    </div>
                  </td>
                </template>
              </tr>

              <!-- Fila nueva al final -->
              <tr v-if="filaEditandoId === '__nueva__'" class="bg-violet-50 dark:bg-violet-900/20 border-b dark:border-gray-700">
                <td class="px-3 py-1.5 text-center"><input type="checkbox" class="rounded" disabled/></td>
                <td class="px-3 py-1.5 text-gray-400 text-center text-xs">auto</td>
                <td class="px-1 py-1"><input v-model="filaForm.id_unidad_medida" type="text" maxlength="30" autofocus placeholder="Ej: cm" class="w-full px-2 py-1 border border-violet-400 rounded text-sm font-mono dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"/></td>
                <td class="px-1 py-1"><input v-model="filaForm.nombre" type="text" maxlength="100" placeholder="Ej: centímetro" class="w-full px-2 py-1 border border-violet-400 rounded text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"/></td>
                <td class="px-1 py-1"><input v-model="filaForm.descripcion" type="text" maxlength="255" placeholder="Descripción..." class="w-full px-2 py-1 border border-violet-400 rounded text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"/></td>
                <td class="px-1 py-1"><input v-model="filaForm.clave_sat" type="text" maxlength="30" placeholder="Ej: H87" class="w-full px-2 py-1 border border-violet-400 rounded text-sm font-mono dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"/></td>
                <td class="px-1 py-1"><input v-model="filaForm.nombre_sat" type="text" maxlength="100" placeholder="Ej: PIEZA" class="w-full px-2 py-1 border border-violet-400 rounded text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"/></td>
                <td class="px-1 py-1">
                  <div class="flex gap-1">
                    <button @click="guardarFila" :disabled="guardandoFila" class="text-green-600 hover:text-green-800 dark:text-green-400 p-1" title="Guardar">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                    </button>
                    <button @click="cancelarFila" class="text-gray-400 hover:text-gray-600 p-1" title="Cancelar">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                    </button>
                  </div>
                </td>
              </tr>

              <tr v-if="unidades.length === 0">
                <td colspan="8" class="px-4 py-8 text-center text-gray-400">No se encontraron unidades de medida</td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Error fila -->
      <div v-if="filaError" class="px-4 py-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800">
        {{ filaError }}
      </div>

      <!-- Paginación catálogo -->
      <div v-if="paginacionUnidades.total > 0" class="flex justify-between items-center px-4 py-3 border-t border-gray-200 dark:border-gray-700">
        <div class="text-sm text-gray-600 dark:text-gray-400">
          Mostrando {{ ((paginacionUnidades.current_page - 1) * paginacionUnidades.per_page) + 1 }}
          a {{ Math.min(paginacionUnidades.current_page * paginacionUnidades.per_page, paginacionUnidades.total) }}
          de {{ paginacionUnidades.total }} registros
        </div>
        <div class="flex gap-2">
          <button @click="cambiarPaginaUnidades(paginacionUnidades.current_page - 1)" :disabled="paginacionUnidades.current_page <= 1" class="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-sm disabled:opacity-50">Anterior</button>
          <span class="px-3 py-1 text-sm text-gray-600 dark:text-gray-400">Página {{ paginacionUnidades.current_page }} de {{ paginacionUnidades.last_page }}</span>
          <button @click="cambiarPaginaUnidades(paginacionUnidades.current_page + 1)" :disabled="paginacionUnidades.current_page >= paginacionUnidades.last_page" class="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-sm disabled:opacity-50">Siguiente</button>
        </div>
      </div>
    </div>

    <!-- ==================== TAB EQUIVALENCIAS ==================== -->
    <div v-if="tabActivo === 'equivalencias'" class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div class="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-end">
        <button
          v-if="eqFilaEditandoId !== '__nueva__'"
          @click="eqAgregarFilaNueva"
          class="px-4 py-2 rounded bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition flex items-center gap-1"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
          Nueva Equivalencia
        </button>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-sm text-left">
          <thead class="text-xs uppercase bg-gray-100 dark:bg-gray-800 text-blue-600 dark:text-blue-400 border-b-2 border-gray-200 dark:border-gray-700">
            <tr>
              <th class="px-3 py-2 w-8"><input type="checkbox" class="rounded"/></th>
              <th class="px-3 py-2 w-8">#</th>
              <th class="px-3 py-2">Unidad Medida Origen</th>
              <th class="px-3 py-2 w-8 text-center">=</th>
              <th class="px-3 py-2 w-36">Equivalencia</th>
              <th class="px-3 py-2">Unidad Medida Destino</th>
              <th class="px-3 py-2 w-20"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loadingEquiv">
              <td colspan="7" class="px-4 py-8 text-center text-gray-400">Cargando...</td>
            </tr>
            <template v-else>
              <tr
                v-for="eq in equivalencias"
                :key="eq.id"
                class="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                :class="eq.id === eqFilaEditandoId ? 'bg-violet-50 dark:bg-violet-900/20' : 'bg-white dark:bg-gray-800'"
              >
                <td class="px-3 py-1.5 text-center"><input type="checkbox" class="rounded"/></td>
                <td class="px-3 py-1.5 text-gray-500 text-center">{{ eq.id }}</td>

                <!-- Lectura -->
                <template v-if="eq.id !== eqFilaEditandoId">
                  <td class="px-3 py-1.5 text-gray-900 dark:text-white">
                    <span class="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded mr-1">{{ eq.unidad_origen?.id_unidad_medida }}</span>{{ eq.unidad_origen?.nombre }}
                  </td>
                  <td class="px-3 py-1.5 text-center font-bold text-gray-400">=</td>
                  <td class="px-3 py-1.5 font-semibold text-gray-900 dark:text-white">{{ eq.factor }}</td>
                  <td class="px-3 py-1.5 text-gray-900 dark:text-white">
                    <span class="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded mr-1">{{ eq.unidad_destino?.id_unidad_medida }}</span>{{ eq.unidad_destino?.nombre }}
                  </td>
                  <td class="px-3 py-1.5">
                    <div class="flex gap-1">
                      <button @click="eqEditarFila(eq)" class="text-blue-600 hover:text-blue-800 dark:text-blue-400 p-1" title="Editar">
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                      </button>
                      <button @click="eliminarEquiv(eq)" class="text-red-500 hover:text-red-700 dark:text-red-400 p-1" title="Eliminar">
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                      </button>
                    </div>
                  </td>
                </template>

                <!-- Edición -->
                <template v-else>
                  <td class="px-1 py-1">
                    <select v-model="eqFilaForm.unidad_origen_id" class="w-full px-2 py-1 border border-violet-400 rounded text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500">
                      <option value="">-- Origen --</option>
                      <option v-for="u in todasUnidades" :key="u.id" :value="u.id">{{ u.id_unidad_medida }} - {{ u.nombre }}</option>
                    </select>
                  </td>
                  <td class="px-1 py-1 text-center font-bold text-gray-400">=</td>
                  <td class="px-1 py-1">
                    <input v-model="eqFilaForm.factor" type="number" min="0.000001" step="any" placeholder="Factor" class="w-full px-2 py-1 border border-violet-400 rounded text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"/>
                  </td>
                  <td class="px-1 py-1">
                    <select v-model="eqFilaForm.unidad_destino_id" class="w-full px-2 py-1 border border-violet-400 rounded text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500">
                      <option value="">-- Destino --</option>
                      <option v-for="u in todasUnidades" :key="u.id" :value="u.id">{{ u.id_unidad_medida }} - {{ u.nombre }}</option>
                    </select>
                  </td>
                  <td class="px-1 py-1">
                    <div class="flex gap-1">
                      <button @click="eqGuardarFila" :disabled="eqGuardando" class="text-green-600 hover:text-green-800 dark:text-green-400 p-1" title="Guardar">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                      </button>
                      <button @click="eqCancelarFila" class="text-gray-400 hover:text-gray-600 p-1" title="Cancelar">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                      </button>
                    </div>
                  </td>
                </template>
              </tr>

              <!-- Fila nueva equivalencia -->
              <tr v-if="eqFilaEditandoId === '__nueva__'" class="bg-violet-50 dark:bg-violet-900/20 border-b dark:border-gray-700">
                <td class="px-3 py-1.5 text-center"><input type="checkbox" class="rounded" disabled/></td>
                <td class="px-3 py-1.5 text-gray-400 text-xs text-center">auto</td>
                <td class="px-1 py-1">
                  <select v-model="eqFilaForm.unidad_origen_id" autofocus class="w-full px-2 py-1 border border-violet-400 rounded text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500">
                    <option value="">-- Unidad Origen --</option>
                    <option v-for="u in todasUnidades" :key="u.id" :value="u.id">{{ u.id_unidad_medida }} - {{ u.nombre }}</option>
                  </select>
                </td>
                <td class="px-1 py-1 text-center font-bold text-gray-400">=</td>
                <td class="px-1 py-1">
                  <input v-model="eqFilaForm.factor" type="number" min="0.000001" step="any" placeholder="Factor" class="w-full px-2 py-1 border border-violet-400 rounded text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500"/>
                </td>
                <td class="px-1 py-1">
                  <select v-model="eqFilaForm.unidad_destino_id" class="w-full px-2 py-1 border border-violet-400 rounded text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-1 focus:ring-violet-500">
                    <option value="">-- Unidad Destino --</option>
                    <option v-for="u in todasUnidades" :key="u.id" :value="u.id">{{ u.id_unidad_medida }} - {{ u.nombre }}</option>
                  </select>
                </td>
                <td class="px-1 py-1">
                  <div class="flex gap-1">
                    <button @click="eqGuardarFila" :disabled="eqGuardando" class="text-green-600 hover:text-green-800 dark:text-green-400 p-1" title="Guardar">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                    </button>
                    <button @click="eqCancelarFila" class="text-gray-400 hover:text-gray-600 p-1" title="Cancelar">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                    </button>
                  </div>
                </td>
              </tr>

              <tr v-if="equivalencias.length === 0">
                <td colspan="7" class="px-4 py-8 text-center text-gray-400">No se encontraron equivalencias</td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Error equivalencias -->
      <div v-if="eqFilaError" class="px-4 py-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800">
        {{ eqFilaError }}
      </div>

      <!-- Paginación equivalencias -->
      <div v-if="paginacionEquiv.total > 0" class="flex justify-between items-center px-4 py-3 border-t border-gray-200 dark:border-gray-700">
        <div class="text-sm text-gray-600 dark:text-gray-400">
          Mostrando {{ ((paginacionEquiv.current_page - 1) * paginacionEquiv.per_page) + 1 }}
          a {{ Math.min(paginacionEquiv.current_page * paginacionEquiv.per_page, paginacionEquiv.total) }}
          de {{ paginacionEquiv.total }} registros
        </div>
        <div class="flex gap-2">
          <button @click="cambiarPaginaEquiv(paginacionEquiv.current_page - 1)" :disabled="paginacionEquiv.current_page <= 1" class="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-sm disabled:opacity-50">Anterior</button>
          <span class="px-3 py-1 text-sm text-gray-600 dark:text-gray-400">Página {{ paginacionEquiv.current_page }} de {{ paginacionEquiv.last_page }}</span>
          <button @click="cambiarPaginaEquiv(paginacionEquiv.current_page + 1)" :disabled="paginacionEquiv.current_page >= paginacionEquiv.last_page" class="px-3 py-1 rounded bg-gray-200 dark:bg-gray-700 text-sm disabled:opacity-50">Siguiente</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import { useHttp } from '@/composables/useHttp.js'
import Swal from 'sweetalert2'

const { get, post, put, delete: del, data } = useHttp()

// ─── Tab ─────────────────────────────────────────────────────────────────────
const tabActivo = ref('catalogo')

// ─── Catálogo ─────────────────────────────────────────────────────────────────
const unidades        = ref([])
const loadingUnidades = ref(false)
const filtros         = ref({ buscar: '' })
const paginacionUnidades = ref({ current_page: 1, last_page: 1, per_page: 15, total: 0 })

// inline edit catálogo
const filaEditandoId = ref(null)   // id numérico o '__nueva__'
const filaForm       = ref(emptyFilaForm())
const guardandoFila  = ref(false)
const filaError      = ref('')

function emptyFilaForm() {
  return { id_unidad_medida: '', nombre: '', descripcion: '', clave_sat: '', nombre_sat: '' }
}

const cargarUnidades = async (pagina = 1) => {
  try {
    loadingUnidades.value = true
    filaError.value = ''
    const params = new URLSearchParams({ page: pagina, per_page: paginacionUnidades.value.per_page })
    if (filtros.value.buscar) params.append('buscar', filtros.value.buscar)
    await get(`/pos2/unidades-medida?${params}`)
    unidades.value = data.value.data
    paginacionUnidades.value = {
      current_page: data.value.current_page,
      last_page:    data.value.last_page,
      per_page:     data.value.per_page,
      total:        data.value.total,
    }
  } finally {
    loadingUnidades.value = false
  }
}

const buscarUnidades       = () => cargarUnidades(1)
const limpiarUnidades      = () => { filtros.value.buscar = ''; cargarUnidades(1) }
const cambiarPaginaUnidades = (p) => { if (p >= 1 && p <= paginacionUnidades.value.last_page) cargarUnidades(p) }

const agregarFilaNueva = () => {
  filaEditandoId.value = '__nueva__'
  filaForm.value = emptyFilaForm()
  filaError.value = ''
}

const editarFila = (unidad) => {
  filaEditandoId.value = unidad.id
  filaForm.value = {
    id_unidad_medida: unidad.id_unidad_medida,
    nombre:           unidad.nombre,
    descripcion:      unidad.descripcion ?? '',
    clave_sat:        unidad.clave_sat   ?? '',
    nombre_sat:       unidad.nombre_sat  ?? '',
  }
  filaError.value = ''
}

const cancelarFila = () => {
  filaEditandoId.value = null
  filaError.value = ''
}

const guardarFila = async () => {
  if (!filaForm.value.id_unidad_medida || !filaForm.value.nombre) {
    filaError.value = 'Id Unidad Medida y Nombre son obligatorios'
    return
  }
  try {
    guardandoFila.value = true
    filaError.value = ''
    if (filaEditandoId.value === '__nueva__') {
      await post('/pos2/unidades-medida', filaForm.value)
    } else {
      await put(`/pos2/unidades-medida/${filaEditandoId.value}`, filaForm.value)
    }
    cancelarFila()
    // también refrescamos lista de todasUnidades para el select de equivalencias
    todasUnidades.value = []
    cargarUnidades(paginacionUnidades.value.current_page)
  } catch (err) {
    const errors = err.response?.data?.errors
    filaError.value = errors
      ? Object.values(errors).flat().join(' | ')
      : (err.response?.data?.message ?? 'Error al guardar')
  } finally {
    guardandoFila.value = false
  }
}

const eliminarUnidad = async (unidad) => {
  const result = await Swal.fire({
    title: '¿Estás seguro?',
    text: `¿Eliminar "${unidad.nombre}"?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar',
  })
  if (result.isConfirmed) {
    try {
      await del(`/pos2/unidades-medida/${unidad.id}`)
      todasUnidades.value = []
      cargarUnidades(paginacionUnidades.value.current_page)
    } catch (err) {
      Swal.fire('Error', 'No se pudo eliminar. Puede tener equivalencias asociadas.', 'error')
    }
  }
}

// ─── Equivalencias ────────────────────────────────────────────────────────────
const equivalencias  = ref([])
const loadingEquiv   = ref(false)
const todasUnidades  = ref([])   // para los selects
const paginacionEquiv = ref({ current_page: 1, last_page: 1, per_page: 15, total: 0 })

const eqFilaEditandoId = ref(null)
const eqFilaForm       = ref(emptyEqForm())
const eqGuardando      = ref(false)
const eqFilaError      = ref('')

function emptyEqForm() {
  return { unidad_origen_id: '', factor: '', unidad_destino_id: '' }
}

const cargarEquivalencias = async (pagina = 1) => {
  try {
    loadingEquiv.value = true
    eqFilaError.value = ''
    const params = new URLSearchParams({ page: pagina, per_page: paginacionEquiv.value.per_page })
    await get(`/pos2/equivalencias-unidad-medida?${params}`)
    equivalencias.value = data.value.data
    paginacionEquiv.value = {
      current_page: data.value.current_page,
      last_page:    data.value.last_page,
      per_page:     data.value.per_page,
      total:        data.value.total,
    }
  } finally {
    loadingEquiv.value = false
  }
}

const cargarTodasUnidades = async () => {
  if (todasUnidades.value.length > 0) return
  await get('/pos2/unidades-medida/todas')
  todasUnidades.value = data.value
}

const cambiarPaginaEquiv = (p) => { if (p >= 1 && p <= paginacionEquiv.value.last_page) cargarEquivalencias(p) }

const eqAgregarFilaNueva = async () => {
  await cargarTodasUnidades()
  eqFilaEditandoId.value = '__nueva__'
  eqFilaForm.value = emptyEqForm()
  eqFilaError.value = ''
}

const eqEditarFila = async (eq) => {
  await cargarTodasUnidades()
  eqFilaEditandoId.value = eq.id
  eqFilaForm.value = {
    unidad_origen_id:  eq.unidad_origen_id,
    factor:            eq.factor,
    unidad_destino_id: eq.unidad_destino_id,
  }
  eqFilaError.value = ''
}

const eqCancelarFila = () => {
  eqFilaEditandoId.value = null
  eqFilaError.value = ''
}

const eqGuardarFila = async () => {
  if (!eqFilaForm.value.unidad_origen_id || !eqFilaForm.value.factor || !eqFilaForm.value.unidad_destino_id) {
    eqFilaError.value = 'Todos los campos son obligatorios'
    return
  }
  try {
    eqGuardando.value = true
    eqFilaError.value = ''
    const payload = {
      unidad_origen_id:  Number(eqFilaForm.value.unidad_origen_id),
      factor:            Number(eqFilaForm.value.factor),
      unidad_destino_id: Number(eqFilaForm.value.unidad_destino_id),
    }
    if (eqFilaEditandoId.value === '__nueva__') {
      await post('/pos2/equivalencias-unidad-medida', payload)
    } else {
      await put(`/pos2/equivalencias-unidad-medida/${eqFilaEditandoId.value}`, payload)
    }
    eqCancelarFila()
    cargarEquivalencias(paginacionEquiv.value.current_page)
  } catch (err) {
    const errors = err.response?.data?.errors
    eqFilaError.value = errors
      ? Object.values(errors).flat().join(' | ')
      : (err.response?.data?.message ?? 'Error al guardar')
  } finally {
    eqGuardando.value = false
  }
}

const eliminarEquiv = async (eq) => {
  const result = await Swal.fire({
    title: '¿Estás seguro?',
    text: `¿Eliminar equivalencia: 1 ${eq.unidad_origen?.id_unidad_medida} = ${eq.factor} ${eq.unidad_destino?.id_unidad_medida}?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar',
  })
  if (result.isConfirmed) {
    try {
      await del(`/pos2/equivalencias-unidad-medida/${eq.id}`)
      cargarEquivalencias(paginacionEquiv.value.current_page)
    } catch (err) {
      Swal.fire('Error', 'No se pudo eliminar', 'error')
    }
  }
}

// Cargar datos al cambiar de tab
watch(tabActivo, (tab) => {
  if (tab === 'equivalencias' && equivalencias.value.length === 0) cargarEquivalencias()
})

onMounted(() => cargarUnidades())
</script>
